/**
 * Date and time utilities to replace date-fns
 * Provides common date manipulation and formatting functions
 */
const MILLISECONDS_IN_SECOND = 1000;
const MILLISECONDS_IN_MINUTE = 60 * MILLISECONDS_IN_SECOND;
/**
 * Format a date to YYYY-MM-DD format
 * @param date - The date to format
 * @returns The formatted date in YYYY-MM-DD format
 */
export function formatYYYYMMDD(date) {
    return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
        .map((part) => part.toString().padStart(2, '0'))
        .join('-');
}
/**
 *
 * @param date - The date to format
 * @returns The formatted date in HH:MM format
 */
export function formatHHMM(date) {
    return [date.getHours(), date.getMinutes()]
        .map((part) => part.toString().padStart(2, '0'))
        .join(':');
}
/**
 * Format a date to YYYY-MM-DDTHH:MM:SS format
 * @param date - The date to format
 * @returns The formatted date in YYYY-MM-DDTHH:MM:SS format
 */
export function formatYYYYMMDDTHHMMSS(date) {
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${formatYYYYMMDD(date)}T${formatHHMM(date)}:${seconds}`;
}
/**
 * Add days to a date
 */
export function addDays(date, amount) {
    const result = new Date(date);
    result.setDate(result.getDate() + amount);
    return result;
}
/**
 * Add weeks to a date
 */
export function addWeeks(date, amount) {
    return addDays(date, amount * 7);
}
/**
 * Add months to a date
 */
export function addMonths(date, amount) {
    const result = new Date(date);
    result.setMonth(result.getMonth() + amount);
    return result;
}
/**
 * Add years to a date
 */
export function addYears(date, amount) {
    const result = new Date(date);
    result.setFullYear(result.getFullYear() + amount);
    return result;
}
/**
 * Convert seconds to milliseconds
 */
export function secondsToMilliseconds(seconds) {
    return seconds * MILLISECONDS_IN_SECOND;
}
/**
 * Get Unix timestamp (in seconds) from a date
 */
export function getUnixTime(date) {
    return Math.floor(new Date(date).getTime() / 1000);
}
/**
 * Get the start of day for a date
 */
export function startOfDay(date) {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
}
/**
 * Check if a date is today
 */
export function isToday(date) {
    const today = startOfDay(new Date());
    const dateToCheck = startOfDay(date);
    return today.getTime() === dateToCheck.getTime();
}
/**
 * Check if a date is yesterday
 */
export function isYesterday(date) {
    const yesterday = startOfDay(addDays(new Date(), -1));
    const dateToCheck = startOfDay(date);
    return yesterday.getTime() === dateToCheck.getTime();
}
/**
 * Check if a date is tomorrow
 */
export function isTomorrow(date) {
    const tomorrow = startOfDay(addDays(new Date(), 1));
    const dateToCheck = startOfDay(date);
    return tomorrow.getTime() === dateToCheck.getTime();
}
/**
 * Check if a date is in the current year
 */
export function isThisYear(date) {
    const currentYear = new Date().getFullYear();
    const dateYear = new Date(date).getFullYear();
    return currentYear === dateYear;
}
/**
 * Calculate the difference in months between two dates
 */
/**
 * Add minutes to a date
 */
export function addMinutes(date, amount) {
    const result = new Date(date);
    result.setMinutes(result.getMinutes() + amount);
    return result;
}
/**
 * Calculate the difference in minutes between two dates
 */
export function differenceInMinutes(dateLeft, dateRight) {
    const leftTime = new Date(dateLeft).getTime();
    const rightTime = new Date(dateRight).getTime();
    return Math.floor((leftTime - rightTime) / MILLISECONDS_IN_MINUTE);
}
/**
 * Get the hours from a date
 */
/**
 * Get the minutes from a date
 */
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/**
 * Convert a UTC date to a specific timezone
 */
export function utcToZonedTime(dateInput, timeZone) {
    const dateObj = new Date(dateInput);
    // Use Intl.DateTimeFormat to get the date in the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        weekday: 'long',
        hour12: false,
    });
    const parts = formatter.formatToParts(dateObj);
    const getValue = (type) => parts.find((p) => p.type === type)?.value || '0';
    const year = parseInt(getValue('year'), 10);
    const month = parseInt(getValue('month'), 10) - 1;
    const date = parseInt(getValue('day'), 10);
    const hour = parseInt(getValue('hour'), 10);
    const minute = parseInt(getValue('minute'), 10);
    const second = parseInt(getValue('second'), 10);
    const weekday = WEEKDAYS.indexOf(getValue('weekday'));
    const epochMs = dateObj.getTime();
    return { year, month, date, hour, minute, second, weekday, epochMs };
}
/**
 * Formats a Date as a short timestamp string using react-intl.
 * Shows time only for today ("2:30 PM"), includes date for older messages ("Mar 8, 2:30 PM").
 * Returns null for invalid dates.
 */
export function formatTimestampShort(date, intl) {
    if (isNaN(date.getTime()))
        return null;
    try {
        const time = intl.formatTime(date, { timeStyle: 'short' });
        if (isToday(date))
            return time;
        const dateStr = intl.formatDate(date, { month: 'short', day: 'numeric' });
        return `${dateStr}, ${time}`;
    }
    catch {
        return null;
    }
}
/**
 * Returns whether a date string parses to a valid date.
 */
export function isValidDate(d) {
    return !isNaN(new Date(d).getTime());
}
const getDateValues = (parts) => ({
    year: parseInt(parts.find((p) => p.type === 'year').value),
    month: parseInt(parts.find((p) => p.type === 'month').value),
    day: parseInt(parts.find((p) => p.type === 'day').value),
});
/**
 * Returns whether a date falls on the current calendar day in a time zone.
 */
export function isTodayInTimezone(date, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });
    const todayParts = formatter.formatToParts(new Date());
    const dateParts = formatter.formatToParts(date);
    const today = getDateValues(todayParts);
    const targetDate = getDateValues(dateParts);
    return (today.year === targetDate.year &&
        today.month === targetDate.month &&
        today.day === targetDate.day);
}
/**
 * Returns whether two dates fall on the same calendar day in a time zone.
 */
export function isSameDayInTimezone(date1, date2, timeZone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    });
    const d1 = getDateValues(formatter.formatToParts(date1));
    const d2 = getDateValues(formatter.formatToParts(date2));
    return d1.year === d2.year && d1.month === d2.month && d1.day === d2.day;
}
/**
 * Checks if a date string (YYYY-MM-DD) is valid and not in the future.
 */
export function isValidPastDate(date) {
    if (!date)
        return false;
    return date <= formatYYYYMMDD(new Date());
}
//# sourceMappingURL=datetime.js.map